import { useQuery } from "@apollo/client";
import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Skeleton,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import { useCombobox, useMultipleSelection } from "downshift";
import _ from "lodash";
import React from "react";
import { getStudentSearchQuery } from "src/components/graphql/utils";
import * as GQL from "src/types/graphql";
import { GET_STUDENTS } from "../students/graphql/queries";

const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;

export interface StudentOption {
  id: string;
  label: string;
}

interface StudentSearchMultiSelectProps {
  organizationId: string;
  selectedStudents: StudentOption[];
  onChange: (students: StudentOption[]) => void;
  isDisabled?: boolean;
  placeholder?: string;
}

function toLabel(person: GQL.GetStudents_person): string {
  return `${person.first_name} ${person.last_name}`.trim();
}

/**
 * Multi-select of students backed by server-side search. Unlike a client-side
 * filtered select, this never eagerly loads the full roster; it queries
 * GET_STUDENTS with a debounced keyword and paginates via infinite scroll, so
 * it scales past any fixed fetch limit. Modeled on PersonSelectInput /
 * EligibleStudentSelectInput but supports selecting multiple students.
 */
export function StudentSearchMultiSelect({
  organizationId,
  selectedStudents,
  onChange,
  isDisabled = false,
  placeholder = "Search for one or more student by name",
}: StudentSearchMultiSelectProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const [inputValue, setInputValue] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [options, setOptions] = React.useState<StudentOption[]>([]);
  const [total, setTotal] = React.useState(0);

  const searchValue = inputValue.trim();

  const [debouncedSearch, setDebouncedSearch] = React.useState(searchValue);
  const debouncedSetSearch = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setDebouncedSearch(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearch(searchValue);
    return () => debouncedSetSearch.cancel();
  }, [searchValue, debouncedSetSearch]);

  // Reset pagination whenever the search term changes.
  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  const canSearch =
    !!organizationId && debouncedSearch.length >= MIN_SEARCH_LENGTH;

  const { data, loading } = useQuery<GQL.GetStudents, GQL.GetStudentsVariables>(
    GET_STUDENTS,
    {
      variables: {
        organizationId,
        search: {
          _and: [
            getStudentSearchQuery(debouncedSearch),
            { deleted_at: { _is_null: true } },
          ],
        },
        limit: PAGE_SIZE,
        offset,
        order_by: [
          { first_name: GQL.order_by.asc },
          { last_name: GQL.order_by.asc },
        ],
      },
      skip: !canSearch,
      fetchPolicy: "network-only",
    }
  );

  React.useEffect(() => {
    if (!canSearch) {
      setOptions([]);
      setTotal(0);
      return;
    }
    if (!data) return;
    const fetched: StudentOption[] = data.person.map((person) => ({
      id: person.id,
      label: toLabel(person),
    }));
    setOptions((prev) =>
      offset === 0 ? fetched : _.unionBy(prev, fetched, "id")
    );
    setTotal(data.person_aggregate.totals?.total ?? 0);
  }, [data, offset, canSearch]);

  const selectedIds = React.useMemo(
    () => new Set(selectedStudents.map((s) => s.id)),
    [selectedStudents]
  );

  const itemsForDropdown = React.useMemo(
    () => options.filter((option) => !selectedIds.has(option.id)),
    [options, selectedIds]
  );

  const fetchMore = React.useCallback(() => {
    if (options.length < total && canSearch && !loading) {
      setOffset(options.length);
    }
  }, [options.length, total, canSearch, loading]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchMore();
        }
      },
      { rootMargin: "20px" }
    );
    const current = observerRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [fetchMore]);

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection<StudentOption>({
      selectedItems: selectedStudents,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            onChange(newSelectedItems ?? []);
            break;
          default:
            break;
        }
      },
    });

  useOutsideClick({
    ref: containerRef,
    handler: () => isOpen && closeMenu(),
  });

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    openMenu,
    closeMenu,
  } = useCombobox<StudentOption>({
    items: itemsForDropdown,
    inputValue,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: (item) => (item ? item.label : ""),
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            inputValue: "",
            highlightedIndex: state.highlightedIndex,
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onChange([...selectedStudents, selectedItem]);
            setInputValue("");
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          openMenu();
          setInputValue(newInputValue ?? "");
          break;
        default:
          break;
      }
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) openMenu();
  }, [isDisabled, openMenu]);

  const noResults =
    canSearch && !loading && total === 0 && itemsForDropdown.length === 0;

  return (
    <Box position="relative" width="100%" ref={containerRef}>
      <FormControl>
        {selectedStudents.length > 0 && (
          <Flex gap={1} flexWrap="wrap" mb={2}>
            {selectedStudents.map((student, index) => (
              <Tag
                key={student.id}
                {...getSelectedItemProps({
                  selectedItem: student,
                  index,
                })}
              >
                <TagLabel>{student.label}</TagLabel>
                <TagCloseButton
                  isDisabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelectedItem(student);
                    onChange(
                      selectedStudents.filter((s) => s.id !== student.id)
                    );
                  }}
                />
              </Tag>
            ))}
          </Flex>
        )}
        <InputGroup onClick={openMenuUnlessDisabled}>
          <InputLeftElement children={<SearchIcon color="gray.500" />} />
          <Input
            {...getInputProps(
              getDropdownProps(
                { preventKeyAction: isOpen },
                { suppressRefError: true }
              )
            )}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        </InputGroup>
        <UnorderedList
          {...getMenuProps()}
          display={isOpen ? "block" : "none"}
          position="absolute"
          background="white"
          marginLeft={0}
          zIndex="2000"
          boxShadow="lg"
          width="100%"
          marginTop={1}
          overflow="auto"
          maxHeight="300px"
        >
          {canSearch ? (
            <>
              {itemsForDropdown.map((option, index) => (
                <ListItem
                  {...getItemProps({ item: option, index })}
                  key={option.id}
                  backgroundColor={
                    highlightedIndex === index ? "blackAlpha.100" : "white"
                  }
                  listStyleType="none"
                  cursor="pointer"
                >
                  <Flex align="center" minHeight="10" padding="3">
                    <Text fontSize="md">{option.label}</Text>
                  </Flex>
                </ListItem>
              ))}
              {noResults && (
                <ListItem textAlign="center" p={3} listStyleType="none">
                  <Text color="gray.500" fontSize="md">
                    No students found
                  </Text>
                </ListItem>
              )}
              {loading && (
                <ListItem p={3} listStyleType="none">
                  <Skeleton height="1.25rem" width="60%" />
                </ListItem>
              )}
              <Box ref={observerRef} height="5px" />
            </>
          ) : (
            <ListItem textAlign="center" p={3} listStyleType="none">
              <Text color="gray.500" fontSize="md">
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </Text>
            </ListItem>
          )}
        </UnorderedList>
      </FormControl>
    </Box>
  );
}
